// Polymarket CLOB v2 signing in the browser.
//
// Two phases:
//   L1 — EOA signs an EIP-712 typed-data challenge once per session. The
//        response carries an API key + secret + passphrase scoped to the
//        EOA. We refer to these as "L2 creds".
//   L2 — Each request is HMAC-SHA256 signed using the L2 secret. The
//        signature binds to (timestamp, method, path, body).
//
// This file owns both — viem handles the EIP-712 part, SubtleCrypto the
// HMAC. The EOA private key never leaves the calling component (it's
// passed in as a hex string, used, and discarded by the helper). PR 7
// adds order signing (the EIP-712 order JSON itself).

import { privateKeyToAccount } from 'viem/accounts';
import type { Hex } from 'viem';

// CLOB v2 challenge domain. The values are fixed by Polymarket and were
// chosen so the message presented to an EOA wallet says "This message
// attests that I control the given wallet."
const CLOB_AUTH_DOMAIN = {
  name: 'ClobAuthDomain',
  version: '1',
  chainId: 137,
} as const;

const CLOB_AUTH_TYPES = {
  ClobAuth: [
    { name: 'address', type: 'address' },
    { name: 'timestamp', type: 'string' },
    { name: 'nonce', type: 'uint256' },
    { name: 'message', type: 'string' },
  ],
} as const;

const CLOB_AUTH_MESSAGE = 'This message attests that I control the given wallet';

export interface L1Challenge {
  address: `0x${string}`;
  signature: Hex;
  timestamp: string;
  nonce: number;
}

// Normalises 0x-prefixed hex; throws KeyImportError on shape mismatch.
export function normalizePrivateKey(input: string): Hex {
  const trimmed = input.trim().toLowerCase();
  const stripped = trimmed.startsWith('0x') ? trimmed.slice(2) : trimmed;
  if (!/^[0-9a-f]{64}$/.test(stripped)) {
    throw new Error('polymarket private key must be 32-byte hex (64 chars, optional 0x prefix)');
  }
  return `0x${stripped}` as Hex;
}

export function addressFromPrivateKey(pk: string): `0x${string}` {
  const account = privateKeyToAccount(normalizePrivateKey(pk));
  return account.address;
}

// Builds + signs the L1 challenge with the supplied private key. The
// caller is responsible for keeping the key out of long-lived storage.
export async function signL1Challenge(
  pk: string,
  opts?: { nonce?: number; timestamp?: string },
): Promise<L1Challenge> {
  const account = privateKeyToAccount(normalizePrivateKey(pk));
  const nonce = opts?.nonce ?? 0;
  const timestamp = opts?.timestamp ?? Math.floor(Date.now() / 1000).toString();
  const signature = await account.signTypedData({
    domain: CLOB_AUTH_DOMAIN,
    types: CLOB_AUTH_TYPES,
    primaryType: 'ClobAuth',
    message: {
      address: account.address,
      timestamp,
      nonce: BigInt(nonce),
      message: CLOB_AUTH_MESSAGE,
    },
  });
  return { address: account.address, signature, timestamp, nonce };
}

// ---------------------------------------------------------------------------
// L2 HMAC signing for derived API credentials.
// ---------------------------------------------------------------------------

export interface PolymarketL2Creds {
  apiKey: string;
  secret: string;
  passphrase: string;
}

export interface PolymarketL2Headers {
  apiKey: string;
  passphrase: string;
  timestamp: string;
  signature: string;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function importHmacKey(secret: string): Promise<CryptoKey> {
  // PM L2 secret is base64url-encoded raw bytes.
  const raw = base64UrlDecode(secret);
  return crypto.subtle.importKey(
    'raw',
    raw as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
}

// Builds the headers triple Polymarket expects on every L2-authenticated
// REST call. `body` is the JSON-stringified body (empty string for GET).
// `path` should include any query string — PM binds the signature to it.
export async function signPolymarketL2(
  creds: PolymarketL2Creds,
  method: string,
  path: string,
  body = '',
): Promise<PolymarketL2Headers> {
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const msg = `${timestamp}${method.toUpperCase()}${path}${body}`;
  const key = await importHmacKey(creds.secret);
  const sigBytes = await crypto.subtle.sign(
    'HMAC',
    key,
    new TextEncoder().encode(msg) as BufferSource,
  );
  return {
    apiKey: creds.apiKey,
    passphrase: creds.passphrase,
    timestamp,
    signature: base64UrlEncode(new Uint8Array(sigBytes)),
  };
}
